import { useEffect, useState } from 'react';
import type { ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Typography } from '@proposify/ui';
import { fontSize } from '@proposify/tokens/semantic-tokens/fontSize';
import { AMOUNT_PATTERN, formatThousands } from './helpers';
import { PanelSectionStyled, SubtotalFieldStyled } from './DiscountSection';
import { subtotalSectionTestIds } from './SubtotalSection.test-ids';

interface SubtotalSectionProps {
  /** Row subtotal as a decimal string; display-only. */
  subtotal: string;
  currencySymbol: string;
  /** True when no PRICE column drives the calculation — the subtotal is manually enterable. */
  isEditable?: boolean;
  /** Fires with the raw decimal string on every valid edit (manual subtotal flow). */
  onSubtotalChange?: (value: string) => void;
}

export const SubtotalSection = ({ subtotal, currencySymbol, isEditable = false, onSubtotalChange }: SubtotalSectionProps) => {
  const { t } = useTranslation();
  // Prop echoes back through Redux after a valid edit; effect re-syncs the draft.
  const [draft, setDraft] = useState<string>(subtotal);
  const isDraftInvalid = draft !== '' && !AMOUNT_PATTERN.test(draft);

  useEffect(() => {
    setDraft(subtotal);
  }, [subtotal]);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const raw = event.target.value;
    setDraft(raw);
    if (raw !== '' && AMOUNT_PATTERN.test(raw)) {
      onSubtotalChange?.(raw);
    }
  };

  return (
    <PanelSectionStyled>
      <Typography fontWeight={600} fontSize={fontSize['3']}>
        {t('pricing.discount.subtotal_label')}
      </Typography>
      <SubtotalFieldStyled
        data-testid={subtotalSectionTestIds.subtotal}
        label={t('pricing.discount.amount')}
        value={isEditable ? draft : formatThousands(subtotal)}
        fullWidth
        size="small"
        disabled={!isEditable}
        error={isEditable && isDraftInvalid}
        helperText={isEditable ? undefined : t('pricing.discount.subtotal_helper')}
        startAdornment={currencySymbol}
        onChange={handleChange}
      />
    </PanelSectionStyled>
  );
};
